import { EventTitleEnum, LanguageCodeEnum, SortOrderEnum } from '../common/enums/common.enum';
import { GroupChildrenTabSortKeyEnum, GroupTeacherTabSortKeyEnum } from '../common/enums/group.enum';
import { ApiResponseDto } from '../common/interfaces/common.interface';
import {
  AdminGroupEventsDto,
  ChildMembershipDto,
  ChildWithoutMembership,
  TeacherEngagementDto,
  UnengagedTeacherDto
} from '../common/interfaces/group.interface';
import api from './api';
import { BasicEventConfig, CreateMultiGroupEventsDto, ParentEventsOptionDto } from '../common/interfaces/event.interface';
import { LocalizedTitle } from '../utilities/helper/language.helper';

export const getTeacherGroupEngagements = async (
  groupId: string,
  page: number,
  perPage: number,
  sortBy?: GroupTeacherTabSortKeyEnum,
  sortOrder?: SortOrderEnum,
  name?: string
): Promise<ApiResponseDto<TeacherEngagementDto[]>> => {
  const params: any = {
    page,
    perPage,
    sortBy,
    sortOrder,
    name
  };

  if (name) {
    params.name = name;
  }

  if (sortBy) {
    params.sortBy = sortBy;
  }

  if (sortOrder) {
    params.sortOrder = sortOrder;
  }

  const response = await api.get(`kindergarten/teacher-engagement/${groupId}`, { params });
  return response.data;
};

export const getChildrenMemberships = async (
  groupId: string,
  page: number,
  perPage: number,
  sortBy?: GroupChildrenTabSortKeyEnum,
  sortOrder?: SortOrderEnum,
  name?: string,
  withHistory?: boolean
): Promise<ApiResponseDto<ChildMembershipDto[]>> => {
  const params: any = {
    page,
    perPage,
    sortBy,
    sortOrder,
    name
  };

  if (name) {
    params.name = name;
  }

  if (sortBy) {
    params.sortBy = sortBy;
  }

  if (sortOrder) {
    params.sortOrder = sortOrder;
  }

  if (withHistory !== undefined) {
    params.withHistory = withHistory;
  }

  const response = await api.get(`child/membership/list/${groupId}`, { params });
  return response.data;
};

export const getUnengagedTeachers = async (groupId: string, kindergartenId: string): Promise<ApiResponseDto<UnengagedTeacherDto[]>> => {
  const response = await api.get(`kindergarten/unengaged-teacher/${groupId}/${kindergartenId}`);
  return response.data;
};

export const getChildrenWithoutMembership = async (kindergartenId: string): Promise<ApiResponseDto<ChildWithoutMembership[]>> => {
  const response = await api.get(`child/without-membership/list/${kindergartenId}`);
  return response.data;
};
export const createTeacherGroupEngagements = async (
  groupId: string,
  kindergartenId: string,
  teachers: string[]
): Promise<ApiResponseDto<void>> => {
  const response = await api.post(`kindergarten/engagement/${kindergartenId}/${groupId}`, {
    teachers
  });
  return response.data;
};

export const createChildMemberships = async (
  groupId: string,
  kindergartenId: string,
  childIds: string[]
): Promise<ApiResponseDto<void>> => {
  const response = await api.post(`child/membership/${kindergartenId}/${groupId}`, {
    childIds
  });
  return response.data;
};

export const changeTeacherGroupEngagementStatus = async (groupId: string, teacherEngagementId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`kindergarten/engagement/${teacherEngagementId}/${groupId}`);
  return response.data;
};

export const deactivateChildMembership = async (groupId: string, childMembershipId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`child/membership/${childMembershipId}/deactivate/group/${groupId}`);
  return response.data;
};

export const getGroupEventsSummary = async (groupId: string): Promise<ApiResponseDto<AdminGroupEventsDto[]>> => {
  const response = await api.get(`events/admin/${groupId}`);
  return response.data;
};

export const getParentEventDetails = async (groupId: string, eventId: string): Promise<ApiResponseDto<BasicEventConfig>> => {
  const response = await api.get(`events/admin/details/${eventId}/${groupId}`);
  return response.data;
};

export const getBasicParentEventSetup = async (
  kindergartenId: string,
  parentEvents: EventTitleEnum[]
): Promise<ApiResponseDto<BasicEventConfig[]>> => {
  const params: any = {};

  if (parentEvents?.length) {
    params.parentEvents = parentEvents.join(',');
  }
  const response = await api.get(`events/parent-basic-setup/${kindergartenId}`, {
    params,
    paramsSerializer: params => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    }
  });

  return response.data;
};

export const getMainEvents = async (kindergartenId: string): Promise<ApiResponseDto<ParentEventsOptionDto[]>> => {
  const response = await api.get(`events/parent-events/${kindergartenId}`);

  return response.data;
};

export const createMultiEventConfiguration = async (
  createMultiEventConfigDto: CreateMultiGroupEventsDto,
  kindergartenId: string
): Promise<void> => {
  const response = await api.post(`events/config/${kindergartenId}`, createMultiEventConfigDto);
  return response.data;
};

export const getAvailableIconsBasedOnParentEvent = async (
  kindergartenId: string,
  parentEvent: EventTitleEnum
): Promise<ApiResponseDto<string[]>> => {
  const response = await api.get(`events/option-icons/${kindergartenId}`, {
    params: { parentEvent }
  });

  return response.data;
};

export const updateChildEventTitle = async (
  childEventId: string,
  groupId: string,
  newTitle: LocalizedTitle,
  lang: LanguageCodeEnum
): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/events/child-event/${childEventId}/title/${groupId}?lang=${lang}`, newTitle);
  return response.data;
};

export const updateOptionTitle = async (
  optionId: string,
  groupId: string,
  newTitle: LocalizedTitle,
  lang: LanguageCodeEnum
): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/events/option/${optionId}/title/${groupId}?lang=${lang}`, newTitle);
  return response.data;
};

export const switchDailyRequiredEvent = async (eventId: string, groupId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/events/switch-daily-required/${eventId}/${groupId}`);
  return response.data;
};
